import { FC, useEffect, useState } from "react"
import { IoMdRemove as HorizontalLineIcon } from "react-icons/io"
import { FaRegCalendar as CalendarIcon } from "react-icons/fa6"
import { useParams } from "react-router-dom"
import dayjs from "dayjs"
import utc from "dayjs/plugin/utc"
import {
  selectCampaignSetupEndDate,
  selectCampaignSetupDomains,
  selectCampaignSetupSchedule,
  selectCampaignSetupSettings,
  selectCampaignSetupStartDate,
  selectCampaignSetupStatus,
  selectCampaignSetupTimezone,
  selectCampaignSetupUseEndDate,
} from "@/redux/campaignSetupSlice"
import { useAppSelector } from "@/redux/hooks"
import { useEffectOnce } from "usehooks-ts"
import Checkbox from "@/components/Checkbox"
import OutlineButton from "@/components/OutlineButton"
import Select from "@/components/Select"
import CampaignsStatusSelect from "@/pages/Campaigns/features/CampaignsStatusSelect"
import {
  useGetSystemValuesQuery,
  useGetTimezonesQuery,
} from "@/redux/services/generalApi"
import { useCampaignSetup } from "../hooks/useCampaignSetup"
import TextField from "@/components/TextField"
import DatePicker from "@/components/DatePicker"
import TimeInput from "@/components/TimeInput"
import Loader from "@/components/Loader"
import SandmanSettingsControls from "@/components/SandmanSettingsControls"
import { MAX_DAILY_VOLUME, MIN_DAILY_VOLUME, formatDate } from "./utils"
import { useGetCampaignQuery } from "@/redux/services/campaignsApi"
import { formatDateToISOString, isWeekday } from "@/utils/dates"
import { useAuth } from "@/hooks/useAuth"
import { selectedDomainsSendCapacity } from "@/utils/domainCapacity"
import { DEFAULT_SANDMAN_CONFIG, normalizeSandmanConfig } from "@/utils/sandman"
import type {
  EditCampaignPayload,
  SandmanConfig,
} from "@/types/entities/campaign"

dayjs.extend(utc)

type Props = {
  onChange: (body: EditCampaignPayload) => void
  onDisabledChange: (disabled: boolean) => void
  onCapacityBlockedChange?: (blocked: boolean) => void
}

const CampaignSettingsView: FC<Props> = ({
  onChange,
  onDisabledChange,
  onCapacityBlockedChange,
}) => {
  const { campaignId = "" } = useParams()
  const { isAdmin } = useAuth()
  const showLegacySchedulingControls = isAdmin === true
  const { data: timezones = [], isLoading: timeZonesLoading } =
    useGetTimezonesQuery(undefined, { skip: !showLegacySchedulingControls })
  const { data: systemValues, isLoading: systemValuesLoading } =
    useGetSystemValuesQuery()
  const { data: campaign, isLoading: campaignLoading } = useGetCampaignQuery(
    { id: campaignId },
    { skip: !campaignId },
  )
  const timezone = useAppSelector(selectCampaignSetupTimezone)
  const settings = useAppSelector(selectCampaignSetupSettings)
  const selectedDomains = useAppSelector(selectCampaignSetupDomains)
  const useEndDate = useAppSelector(selectCampaignSetupUseEndDate)
  const startDate = useAppSelector(selectCampaignSetupStartDate)
  const endDate = useAppSelector(selectCampaignSetupEndDate)
  const status = useAppSelector(selectCampaignSetupStatus)
  const {
    daysOfWeek,
    hours: { from, to },
  } = useAppSelector(selectCampaignSetupSchedule)
  const { dailyLimit } = settings
  const estimatedDailyCapacity = selectedDomainsSendCapacity(selectedDomains)
  const estimatedDailyCapacityText = estimatedDailyCapacity
    ? `${estimatedDailyCapacity.toLocaleString()} prospects/day`
    : "unavailable"
  const selectedCapacityExceeded =
    selectedDomains.length > 0 &&
    estimatedDailyCapacity > 0 &&
    dailyLimit > estimatedDailyCapacity
  const selectedCapacityUnavailable =
    selectedDomains.length === 0 ||
    (selectedDomains.length > 0 && estimatedDailyCapacity <= 0)
  const [sandman, setSandman] = useState<SandmanConfig>(DEFAULT_SANDMAN_CONFIG)
  const {
    changeTimezone,
    changeUseEndDate,
    changeDate,
    changeDailyLimits,
    changeSchedule,
    changeStatus,
  } = useCampaignSetup()
  useEffectOnce(() => {
    if (!daysOfWeek.length && systemValues) {
      changeSchedule({
        daysOfWeek: systemValues.daysOfWeek.map(({ id }) => id),
        hours: campaign?.schedule?.hours ?? {
          from,
          to,
        },
      })
    }
  })
  useEffect(() => {
    if (!campaign?._id) {
      return
    }

    setSandman(
      normalizeSandmanConfig(campaign.sandman, campaign.placementGating),
    )
  }, [campaign?._id, campaign?.placementGating, campaign?.sandman])
  useEffect(() => {
    onChange(
      Object.assign(
        {
          useEndDate,
          settings: JSON.stringify({
            dailyLimit,
          }),
          schedule: {
            daysOfWeek,
            hours: {
              from,
              to,
            },
          },
          tz: timezone,
          dailyLimit,
          sandman,
        },
        startDate
          ? {
              startDate: formatDateToISOString(startDate),
            }
          : {},
        useEndDate && endDate
          ? {
              endDate: formatDateToISOString(endDate, "23:59:59.999"),
            }
          : {},
      ),
    )
  }, [
    dailyLimit,
    daysOfWeek,
    endDate,
    from,
    onChange,
    sandman,
    startDate,
    timezone,
    to,
    useEndDate,
  ])
  useEffect(() => {
    onDisabledChange(
      (useEndDate && !endDate) ||
        dailyLimit < MIN_DAILY_VOLUME ||
        (showLegacySchedulingControls && !daysOfWeek.length) ||
        !startDate,
    )
    return () => {
      onDisabledChange(false)
    }
  }, [
    onDisabledChange,
    useEndDate,
    endDate,
    dailyLimit,
    daysOfWeek,
    startDate,
    showLegacySchedulingControls,
  ])
  useEffect(() => {
    onCapacityBlockedChange?.(
      selectedCapacityExceeded || selectedCapacityUnavailable,
    )
    return () => {
      onCapacityBlockedChange?.(false)
    }
  }, [
    onCapacityBlockedChange,
    selectedCapacityExceeded,
    selectedCapacityUnavailable,
  ])

  const handleToggleDays = (dayIndex: number) => {
    const dayPresentIndex = daysOfWeek.findIndex((v) => v === dayIndex)
    let newDays = [...daysOfWeek]
    if (dayPresentIndex === -1) {
      newDays = newDays.concat(dayIndex)
    } else {
      newDays.splice(dayPresentIndex, 1)
    }
    changeSchedule({
      hours: {
        from,
        to,
      },
      daysOfWeek: newDays,
    })
  }
  const handleDailyLimitChange = (v: number) => {
    changeDailyLimits({
      dailyLimit: v,
    })
  }
  const handleChangeDailyLimit = ({
    target: { value },
  }: React.ChangeEvent<HTMLInputElement>) => {
    if (value.length > MAX_DAILY_VOLUME.toString().length) {
      return
    }
    const parsedValue = value ? parseInt(value) : 0
    if (parsedValue > MAX_DAILY_VOLUME) {
      handleDailyLimitChange(MAX_DAILY_VOLUME)
      return
    }
    handleDailyLimitChange(value ? parseInt(value) : 0)
  }
  const utcDate = dayjs.utc()
  const startDateMinDate = new Date(
    Date.UTC(utcDate.year(), utcDate.month(), utcDate.date()) -
      dayjs().utcOffset() * 60000,
  )
  const loading =
    systemValuesLoading ||
    (showLegacySchedulingControls && timeZonesLoading) ||
    campaignLoading ||
    !systemValues ||
    (Boolean(campaignId) && !campaign)
  return (
    <div className="flex flex-col gap-5">
      <h3 className="text-lg leading-7 font-semibold">Settings</h3>
      {loading ? (
        <div className="mt-10 flex justify-center items-center">
          <Loader />
        </div>
      ) : (
        <div className="flex gap-5 flex-col lg:flex-row">
          <div className="flex flex-col gap-5 flex-1 [&_button]:!pr-2">
            {showLegacySchedulingControls && (
              <Select
                value={timezone}
                onChange={(timezone) => changeTimezone(timezone)}
                className="h-10 bg-input rounded-md"
                containerClassName="[&]:!gap-0 [&_.label-text]:py-2"
                bordered={false}
                label="Time Zone"
                placeholder="Select Timezone"
                options={timezones.map(({ display, abbr }) => ({
                  label: `${abbr} (${display})`,
                  value: abbr,
                }))}
              />
            )}
            <div className="flex flex-col gap-2 lg:flex-row items-center">
              <div className="flex flex-col flex-1">
                <DatePicker
                  topLeftLabel="Starting Date"
                  selected={startDate ? startDate : null}
                  onChange={(value) => {
                    if (!value) {
                      return
                    }
                    changeDate("start", value)
                  }}
                  suffixIcon={<CalendarIcon className="w-5 h-5 text-black" />}
                  disabled={Boolean(campaign?.publishedAt)}
                  placeholderText={dayjs().format("M/D/YYYY")}
                  filterDate={isWeekday}
                  formatTextFieldValue={(d) => formatDate(d)}
                  minDate={startDateMinDate}
                  maxDate={
                    useEndDate && endDate
                      ? dayjs(endDate).utc().subtract(3, "days").toDate()
                      : undefined
                  }
                  showTimeInput={false}
                />
                <div className="hidden lg:flex items-center mt-2 h-6"></div>
              </div>
              <HorizontalLineIcon className="h-5 -mb-3.5 lg:-mb-0 w-5 rotate-90 lg:rotate-0" />
              <div className="flex flex-col flex-1">
                <DatePicker
                  topLeftLabel="End Date"
                  disabled={!useEndDate}
                  selected={endDate ? endDate : null}
                  filterDate={isWeekday}
                  onChange={(value) => {
                    if (!value) {
                      return
                    }
                    changeDate("end", value)
                  }}
                  placeholderText={dayjs().add(1, "day").format("M/D/YYYY")}
                  suffixIcon={<CalendarIcon className="w-5 h-5 text-black" />}
                  formatTextFieldValue={(d) => formatDate(d)}
                  minDate={dayjs(startDate).utc().add(3, "day").toDate()}
                  showTimeInput={false}
                />
                <div className="flex items-center mt-2">
                  <Checkbox
                    id="useEndDate"
                    className="checkbox-primary w-[18px] h-[18px]"
                    checked={useEndDate}
                    onChange={({ target: { checked } }) =>
                      changeUseEndDate(checked)
                    }
                  />
                  <label
                    className="pl-2.5 cursor-pointer text-sm leading-6"
                    htmlFor="useEndDate"
                  >
                    Use End Date
                  </label>
                </div>
              </div>
            </div>
            {!showLegacySchedulingControls && (
              <div
                className="rounded-md bg-primary-glass px-3 py-2 text-sm text-text-light"
                data-testid="campaign-quick-mode"
              >
                Quick Mode uses the default time zone, sending window, and
                weekdays.
              </div>
            )}
            {showLegacySchedulingControls && (
              <>
                <div>
                  <h4 className="label-text font-medium text-black pb-2">
                    Sending Window
                  </h4>
                  <div className="flex flex-col gap-2 lg:flex-row items-center">
                    <TimeInput
                      date={new Date(new Date().setHours(from))}
                      onChange={(value) => {
                        if (!value) {
                          return
                        }
                        changeSchedule({
                          daysOfWeek,
                          hours: {
                            from: value.hour(),
                            to,
                          },
                        })
                      }}
                      timeInputFormat="hh:00"
                    />
                    <HorizontalLineIcon className="h-5 w-5 rotate-90 lg:rotate-0" />
                    <TimeInput
                      date={new Date(new Date().setHours(to))}
                      onChange={(value) => {
                        if (!value) {
                          return
                        }
                        changeSchedule({
                          daysOfWeek,
                          hours: {
                            to: value.hour(),
                            from,
                          },
                        })
                      }}
                      timeInputFormat="hh:00"
                    />
                  </div>
                </div>
                <div className="flex flex-col gap-2">
                  <span className="text-sm font-medium">Sending Days</span>
                  <div className="flex gap-2.5 flex-wrap [&>button]:flex-1">
                    {systemValues.daysOfWeek.map(({ display, id }) => (
                      <OutlineButton
                        key={id}
                        active={daysOfWeek.findIndex((v) => v === id) !== -1}
                        className="btn-xs text-black font-medium"
                        onClick={() => handleToggleDays(id)}
                      >
                        {display.slice(0, 3)}
                      </OutlineButton>
                    ))}
                  </div>
                </div>
              </>
            )}
          </div>
          <div className="border-[0.5px] border-outline"></div>
          <div className="flex-1 flex flex-col gap-5">
            <div className="flex flex-col">
              <span className="text-sm font-medium py-2">Status</span>
              <CampaignsStatusSelect
                className="h-10 bg-input rounded-md"
                bordered={false}
                value={status.value}
                onChange={({ value }) => changeStatus(value)}
                popupMatchSelectWidth
              />
            </div>
            <TextField
              type="number"
              value={settings.dailyLimit || ""}
              onChange={handleChangeDailyLimit}
              topLeftLabel="Daily Volume"
              className="h-10 bg-input rounded-md text-sm"
            />
            {selectedDomains.length > 0 && (
              <div
                className={`text-sm leading-6 ${
                  selectedCapacityExceeded || selectedCapacityUnavailable
                    ? "text-error"
                    : "text-text-light"
                }`}
                data-testid="campaign-estimated-daily-capacity"
              >
                {selectedCapacityUnavailable
                  ? "Selected domains do not have sending capacity right now."
                  : `Selected domains can send up to ${estimatedDailyCapacityText}`}
                {selectedCapacityExceeded
                  ? ". Lower daily volume or add another domain."
                  : ""}
              </div>
            )}
            <div className="rounded-md border border-outline bg-bg-default px-4 py-3">
              <div className="flex items-center gap-3">
                <h4 className="whitespace-nowrap text-sm font-medium text-black">
                  Sandman
                </h4>
                <div className="h-px flex-1 bg-outline"></div>
              </div>
              <div className="mt-3">
                <SandmanSettingsControls
                  value={sandman}
                  testIdPrefix="campaign-sandman"
                  description="Run Sandman per provider and choose whether a pass is required before sending."
                  onChange={setSandman}
                />
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default CampaignSettingsView
